/**
 * Helpers for admin screens that operate on behalf of a specific owner.
 *
 * Every API method automatically appends `?ownerId=<id>` to the URL so the
 * backend's `resolveOwnerForRequest()` / `requireOwnerOrPermission()` middleware
 * scopes the call to that owner. Owner-side endpoints under
 * `requireOwnerOrPermission(...)` accept this for admin role.
 */
import { api, apiGet, apiPost, apiPatch, apiDelete } from './client';

function attach(url: string, ownerId: string): string {
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}ownerId=${encodeURIComponent(ownerId)}`;
}

export function makeOwnerApi(ownerId: string) {
  return {
    get<T = unknown>(url: string) {
      return apiGet<T>(attach(url, ownerId));
    },
    post<T = unknown>(url: string, body?: unknown) {
      return apiPost<T>(attach(url, ownerId), body);
    },
    patch<T = unknown>(url: string, body?: unknown) {
      return apiPatch<T>(attach(url, ownerId), body);
    },
    put<T = unknown>(url: string, body?: unknown) {
      return api<T>(attach(url, ownerId), { method: 'PUT', body });
    },
    delete<T = unknown>(url: string) {
      return apiDelete<T>(attach(url, ownerId));
    },
    attach: (url: string) => attach(url, ownerId),
  };
}

export type OwnerApi = ReturnType<typeof makeOwnerApi>;
